import type { VmetaPermissionV2 } from "common";

export type ShareRow = {
  type: "everyone" | "user" | "group";
  email?: string;
  userId?: string;
  groupId?: string;
  /** Set by rowsFromPerms for a user the server held by id with no email (unknown or removed). */
  unknown?: boolean;
  read: boolean;
  write: boolean;
};

/** Stored entries as dialog rows. A user the server could not name keeps its id, an empty email and `unknown`. */
export const rowsFromPerms = (perms: VmetaPermissionV2): ShareRow[] =>
  perms.map((p): ShareRow => {
    if (p.type === "user") {
      const row: ShareRow = {
        type: "user",
        userId: p.userId,
        email: p.email ?? "",
        read: !!p.read,
        write: !!p.write,
      };
      if (!p.email) row.unknown = true;
      return row;
    }
    if (p.type === "group") {
      return {
        type: "group",
        groupId: p.groupId,
        read: !!p.read,
        write: !!p.write,
      };
    }
    return { type: "everyone", read: !!p.read, write: !!p.write };
  });

const keyOf = (r: ShareRow) =>
  r.type === "everyone"
    ? "everyone"
    : r.type === "group"
      ? `group:${r.groupId}`
      : r.email
        ? `email:${r.email.trim().toLowerCase()}`
        : `id:${r.userId}`;

/** Rows as the PUT body: by email when there is one, else by the stored id. Everyone is read only. */
export const payloadFromRows = (rows: ShareRow[]): VmetaPermissionV2 => {
  const merged = new Map<string, ShareRow>();
  for (const r of rows) {
    if (!r.type || !(r.read || r.write)) continue;
    const row = r.type === "everyone" ? { ...r, read: true, write: false } : r;
    const key = keyOf(row);
    const prev = merged.get(key);
    merged.set(
      key,
      prev
        ? {
            ...prev,
            read: prev.read || row.read,
            write: prev.write || row.write,
          }
        : row
    );
  }
  return [...merged.values()].map(r => {
    if (r.type === "everyone") {
      return { type: "everyone", read: true, write: false };
    }
    if (r.type === "group") {
      return {
        type: "group",
        groupId: r.groupId ?? "",
        read: r.read,
        write: r.write,
      };
    }
    return r.email
      ? { type: "user", email: r.email.trim(), read: r.read, write: r.write }
      : { type: "user", userId: r.userId ?? "", read: r.read, write: r.write };
  }) as VmetaPermissionV2;
};
